import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Close,
  Schedule,
  Build,
  CalendarToday,
  LocationOn,
  Person,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface ScheduleInspectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSchedule: (inspectionData: InspectionData) => Promise<void>;
  vehicle: Vehicle | null;
}

interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  vin?: string;
  inspectionDetails?: {
    lastInspection?: string;
    nextInspectionDue?: string;
  };
}

interface InspectionData {
  vehicleId: string;
  scheduledDate: string;
  scheduledTime: string;
  inspectionType: 'routine' | 'safety' | 'damage' | 'pre_rental' | 'post_rental';
  location: string;
  inspector?: string;
  priority: 'low' | 'medium' | 'high';
  notes?: string;
}

const inspectionTypes = [
  { value: 'routine', label: 'Routine Inspection', icon: <CheckCircle />, color: 'primary' },
  { value: 'safety', label: 'Safety Inspection', icon: <Warning />, color: 'warning' },
  { value: 'damage', label: 'Damage Assessment', icon: <Build />, color: 'error' },
  { value: 'pre_rental', label: 'Pre-Rental Inspection', icon: <Schedule />, color: 'info' },
  { value: 'post_rental', label: 'Post-Rental Inspection', icon: <Schedule />, color: 'secondary' }
];

const priorities = [
  { value: 'low', label: 'Low Priority', color: 'success' },
  { value: 'medium', label: 'Medium Priority', color: 'warning' },
  { value: 'high', label: 'High Priority', color: 'error' }
];

const timeSlots = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', 
  '14:00', '15:00', '16:00', '17:00', '18:00'
];

export default function ScheduleInspectionDialog({ 
  open, 
  onClose, 
  onSchedule, 
  vehicle 
}: ScheduleInspectionDialogProps) {
  const theme = useTheme();
  
  const [formData, setFormData] = useState<Omit<InspectionData, 'vehicleId'>>({
    scheduledDate: '',
    scheduledTime: '10:00',
    inspectionType: 'routine',
    location: '',
    inspector: '',
    priority: 'medium',
    notes: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!vehicle) return;
    
    if (!formData.scheduledDate || !formData.location) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const inspectionData: InspectionData = {
        vehicleId: vehicle.id,
        ...formData
      };
      
      await onSchedule(inspectionData);
      handleClose();
    } catch (error) {
      console.error('Failed to schedule inspection:', error);
      alert(`Failed to schedule inspection: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      scheduledDate: '',
      scheduledTime: '10:00',
      inspectionType: 'routine',
      location: '',
      inspector: '',
      priority: 'medium',
      notes: ''
    });
    onClose();
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
    return sixMonthsFromNow.toISOString().split('T')[0];
  };

  if (!vehicle) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'warning.main' }}>
            <Schedule />
          </Avatar>
          <Box>
            <Typography variant="h6">Schedule Inspection</Typography>
            <Typography variant="body2" color="text.secondary">
              {vehicle.year} {vehicle.make} {vehicle.model} • {vehicle.licensePlate}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflow: 'auto' }}>
        <Box sx={{ mt: 2 }}>
          {/* Vehicle Info Summary */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="subtitle2">Current Inspection Status</Typography>
              <Typography variant="body2">
                Last Inspection: {vehicle.inspectionDetails?.lastInspection || 'No previous inspection'}
              </Typography>
              <Typography variant="body2">
                Next Due: {vehicle.inspectionDetails?.nextInspectionDue || 'Not scheduled'}
              </Typography>
              {vehicle.vin && (
                <Typography variant="body2">
                  VIN: {vehicle.vin}
                </Typography>
              )}
            </Box>
          </Alert>

          <Grid container spacing={3}>
            {/* Inspection Type */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Inspection Type</Typography>
              <FormControl fullWidth required>
                <InputLabel>Select Inspection Type</InputLabel>
                <Select
                  value={formData.inspectionType}
                  label="Select Inspection Type"
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    inspectionType: e.target.value as any 
                  }))}
                >
                  {inspectionTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Date and Time */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Inspection Date"
                value={formData.scheduledDate}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: getMinDate(),
                  max: getMaxDate()
                }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Inspection Time</InputLabel>
                <Select
                  value={formData.scheduledTime}
                  label="Inspection Time"
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduledTime: e.target.value }))}
                >
                  {timeSlots.map(time => (
                    <MenuItem key={time} value={time}>{time}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Inspection Location"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Main Service Center, 123 Service Rd, City"
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                required
              />
            </Grid>

            {/* Inspector and Priority */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Inspector (Optional)"
                value={formData.inspector}
                onChange={(e) => setFormData(prev => ({ ...prev, inspector: e.target.value }))}
                placeholder="Enter inspector name or leave blank to auto-assign"
                InputProps={{
                  startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                >
                  {priorities.map(priority => (
                    <MenuItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Special Instructions / Notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any special instructions for the inspector or specific areas of concern..."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Inspection Checklist Preview */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Inspection Checklist Preview</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The following items will be checked during the {inspectionTypes.find(t => t.value === formData.inspectionType)?.label}:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText primary="Exterior condition and damage assessment" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText primary="Interior cleanliness and functionality" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText primary="Mechanical systems and safety features" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText primary="Documentation and compliance verification" />
              </ListItem>
              {formData.inspectionType === 'safety' && (
                <ListItem>
                  <ListItemIcon><Warning color="warning" /></ListItemIcon>
                  <ListItemText primary="Comprehensive safety system diagnostics" />
                </ListItem>
              )}
            </List>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.scheduledDate || !formData.location}
          startIcon={<Schedule />}
        >
          {isSubmitting ? 'Scheduling...' : 'Schedule Inspection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}